import { useEffect, useRef, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { Marke } from '../screens/Marke.js'
import { api, logoUrl } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { db, getMeta, setMeta, META_KEYS } from '../net/localDb.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Der Sperrbildschirm.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Wozu, wenn es doch eine Untätigkeitsfrist gibt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Frist beendet die Sitzung – nach acht Stunden im Büro. Der
 * Sperrbildschirm legt sich nach fünfzehn Minuten darüber und beendet gar
 * nichts. Das ist der Unterschied, und er ist der ganze Zweck:
 *
 *   Wer in die Kaffeepause geht und zurückkommt, tippt sein Passwort und
 *   arbeitet an derselben halbfertigen Rechnung weiter. Nichts geht verloren,
 *   nichts muss neu geladen werden.
 *
 *   Wer in der Zwischenzeit an den offenen Laptop tritt, sieht nichts.
 *
 * Eine Sitzung dafür zu beenden wäre das falsche Werkzeug: der Betrieb
 * verlöre den Bildschirm, auf dem er gerade war, und würde die Sperre nach
 * dem zweiten Mal abschalten wollen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was diese Sperre NICHT ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Sie ist ein Vorhang, kein Tresor. Die Daten liegen weiterhin im
 * Zwischenspeicher des Browsers; wer das Gerät hat und sich auskennt, liest
 * sie dort aus, ohne diesen Bildschirm je zu sehen.
 *
 * Dagegen hilft nur zweierlei, und beides gibt es: die Verschlüsselung der
 * Festplatte – Sache des Geräts, nicht unsere – und das Beenden der
 * Anmeldungen unter Einstellungen → Sicherheit, das sofort wirkt und
 * verhindert, dass von dort weiter abgeglichen wird.
 *
 * Das hier deckt den häufigen Fall ab: den unbeaufsichtigten Bildschirm im
 * Büro, am Empfang, im Zug.
 */

/**
 * Der Anfangsbuchstabe des Betriebs für die Kachel.
 *
 * Dieselbe Regel wie in der Seitenleiste, und aus demselben Grund nicht
 * `firma[0]`: «Ärni Bodenbeläge» fängt mit einem Zeichen an, das der Browser
 * zerlegt. Gesucht ist der erste Buchstabe oder die erste Ziffer.
 */
function ersterBuchstabe(firma: string): string {
  const treffer = firma.trim().match(/\p{L}|\p{N}/u)
  return (treffer?.[0] ?? 'R').toUpperCase()
}

/** Nach wie langer Abwesenheit der Vorhang fällt. */
const SPERRE_NACH_MS = 15 * 60 * 1000

/** Wie oft die letzte Benutzung festgehalten wird, solange jemand arbeitet. */
const TAKT_MS = 60 * 1000

export function Sperre({
  session,
  inaktivMs,
  aufZustand,
  aufAbmelden,
  aufStartseite,
  kinder
}: {
  session: SessionUser
  /**
   * Wie lange die Sitzung laut SERVER unbenutzt war.
   *
   * Die zweite Quelle. Sie fängt den Fall ab, in dem die lokale Spur fehlt –
   * frischer Browser, gelöschte Websitedaten, privates Fenster. Ohne sie
   * genügte «Websitedaten löschen», um an der Sperre vorbeizukommen.
   */
  inaktivMs?: number
  /**
   * Der Sperrentscheid, nach oben gemeldet.
   *
   *   null   noch nicht entschieden
   *   true   gesperrt
   *   false  offen
   *
   * Nicht für die Anzeige – der Vorhang hängt hier. Sondern dafür, dass die
   * Anwendung NICHTS zum Server schickt, bevor dieser Entscheid gefallen ist,
   * und nichts mehr schickt, solange der Vorhang hängt.
   *
   * Der Grund steht am Server: `last_seen_at` misst jetzt echte Benutzung, und
   * der Abgleich im Hintergrund IST echte Benutzung. Liefe er hinter dem
   * Vorhang weiter, hielte er die Sitzung frisch – und der Nächste, der die
   * Websitedaten löscht und neu lädt, bekäme vom Server wieder «zwei Sekunden»
   * zu hören. Die Lücke wäre über den Umweg wieder offen.
   */
  aufZustand?: (gesperrt: boolean | null) => void
  /**
   * Der Weg hinaus.
   *
   * Bis zu diesem Paket gab es keinen: Wer hier stand und sein Passwort
   * nicht hatte – weil der Vorhang dem Vorgänger gehörte, weil das falsche
   * Konto angemeldet war –, kam nicht weg. Ein neuer Tab führte an dieselbe
   * Stelle, denn die Merker liegen am Gerät und nicht am Fenster.
   *
   * Beendet dieselbe Abmeldung wie im angemeldeten Bereich: Sitzung beim
   * Server beenden, Zwischenspeicher leeren, Abgleich anhalten.
   */
  aufAbmelden?: () => Promise<void>
  /**
   * Der Weg auf die öffentliche Startseite.
   *
   * Zweiter Ausgang, und keine Hintertür: Die Startseite ist öffentlich,
   * der angemeldete Bereich bleibt ungezeichnet, und wer zurückkommt, steht
   * wieder vor demselben Vorhang – die Merker liegen am Gerät.
   */
  aufStartseite?: () => void
  kinder: JSX.Element
}): JSX.Element {
  const [gesperrt, setGesperrt] = useState(false)
  const [passwort, setPasswort] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldetAb, setMeldetAb] = useState(false)
  const feld = useRef<HTMLInputElement | null>(null)

  /*
    Der Betrieb, dem dieser Bildschirm gehört.

    Aus dem ZWISCHENSPEICHER, nicht vom Server. Das ist keine Sparsamkeit,
    sondern Teil der Sperre: Hinter dem Vorhang geht keine Anfrage hinaus,
    ausser der einen, mit der jemand sein Passwort einlöst. Eine Abfrage der
    Einstellungen wäre echte Benutzung und hielte die Sitzung frisch – genau
    die Lücke, die dieser Bildschirm schliessen soll.

    Der Name steht notfalls auch in der Sitzung (`tenantName`); gefragt ist
    der Firmenname aus den Einstellungen, denn der steht auch auf den
    Rechnungen.

    ──────────────────────────────────────────────────────────────────────────
    Warum der Zwischenspeicher erst ausgewiesen werden muss
    ──────────────────────────────────────────────────────────────────────────

    Der Zwischenspeicher gehört dem GERÄT, nicht der Sitzung. Auf einem
    Bürolaptop, an dem vorher ein anderes Konto angemeldet war, liegt dort der
    Betrieb des Vorgängers – bis `bindeAn()` ihn leert.

    Genau das ist hier einmal schiefgegangen: Der Sperrbildschirm zeigte den
    Firmennamen eines fremden Betriebs. Nicht weil der Server ihn
    herausgegeben hätte – er tut es nicht –, sondern weil dieser Bildschirm
    las, bevor der Zwischenspeicher dem angemeldeten Betrieb zugeordnet war.

    Deshalb der Ausweis: `current_tenant` sagt, für wen der Zwischenspeicher
    gefüllt wurde. Stimmt er nicht mit der Sitzung überein – oder fehlt er –,
    wird nichts daraus genommen. Dann steht der Name aus der Sitzung da, die
    vom Server kommt und nicht vom Gerät, und statt eines möglicherweise
    fremden Bildes die Kachel. Im Zweifel die neutrale Antwort, nie die
    hübschere.
  */
  const [marke, setMarke] = useState<{ firma: string; logoKey: string | null }>({
    firma: session.tenantName ?? '',
    logoKey: null
  })
  const [logoFehlt, setLogoFehlt] = useState(false)
  useEffect(() => {
    let weg = false
    void (async () => {
      try {
        const gebunden = await getMeta<string | null>(META_KEYS.currentTenant, null)
        if (weg) return
        /*
          Fail-closed. Kein Ausweis, falscher Ausweis: nichts aus dem
          Zwischenspeicher. Der Anfangszustand – Name aus der Sitzung, kein
          Logo – bleibt stehen.
        */
        if (gebunden !== session.tenantId) return
        const row = await db.settings.get('tenant')
        if (weg || !row) return
        const r = row as Record<string, unknown>
        setMarke({
          firma: String(r.companyName ?? session.tenantName ?? ''),
          logoKey: r.logoKey ? String(r.logoKey) : null
        })
      } catch {
        /* Gesperrter Speicher: der Anfangszustand trägt. */
      }
    })()
    return () => {
      weg = true
    }
  }, [session.tenantId, session.tenantName])

  /*
    Die Auskunft des Servers als eigener Zustand – und nicht direkt als Prop.

    Sie ist eine Momentaufnahme: «so lange war diese Sitzung unbenutzt, als
    /me antwortete». Nach einem erfolgreichen Entsperren stimmt sie nicht mehr,
    die Prop steht aber unverändert da, bis die Seite neu geladen wird.

    Ohne diese Trennung hätte das Schliessen der Lücke einen neuen Fehler
    erzeugt: Wer nach 90 Minuten sein richtiges Passwort eingibt, arbeitet
    weiter – und beim ersten Fensterwechsel liefe `pruefen()` erneut, läse
    dieselben 90 Minuten aus der Prop und hinge den Vorhang wieder hin.
    Endlos, denn die Prop ändert sich nie.

    Solange die Lücke offen war, fiel das nicht auf: Die Prop trug nach jedem
    Neuladen zwei Sekunden, und die dritte Frage unten schlug nie an.
  */
  const [serverInaktivMs, setServerInaktivMs] = useState<number | undefined>(inaktivMs)
  useEffect(() => {
    setServerInaktivMs(inaktivMs)
  }, [inaktivMs])

  /* Der Rückkanal darf den Haken unten nicht bei jedem Zeichnen neu starten. */
  const melden = useRef(aufZustand)
  melden.current = aufZustand

  /*
    Ob der angemeldete Bereich überhaupt schon gezeichnet werden darf.

    ──────────────────────────────────────────────────────────────────────────
    Warum das nötig ist – gemessen, nicht überlegt
    ──────────────────────────────────────────────────────────────────────────

    Der Vorhang lag bisher ÜBER dem angemeldeten Bereich, und der Bereich lief
    darunter weiter. Auf einem frisch geladenen Bildschirm heisst das: Das
    Dashboard wird gezeichnet, es holt sich `/dashboard`, und `/dashboard` ist
    echte Benutzung – zu Recht.

    In `proben/sitzung-probe.mts` gemessen, nachdem `last_seen_at` nur noch bei
    echter Benutzung aufgefrischt wird: Der Vorhang fiel richtig, und drei
    Anfragen später stand in der Datenbank trotzdem «vor null Minuten
    benutzt». Websitedaten löschen und noch einmal laden – offen. Die Lücke
    wäre über den Umweg wieder da gewesen.

    Deshalb: Vor dem ersten «nicht gesperrt» wird der Bereich gar nicht erst
    gezeichnet. Danach bleibt er stehen, auch wenn der Vorhang später fällt –
    und genau das ist der Zweck der Sperre: Wer aus der Kaffeepause kommt,
    tippt sein Passwort und arbeitet an derselben halbfertigen Rechnung
    weiter. Beim Laden gibt es noch keine halbfertige Rechnung, die man
    verlieren könnte; mitten in der Arbeit gibt es sie, und dann ist längst
    freigegeben.
  */
  const [freigegeben, setFreigegeben] = useState(false)

  /*
    Die letzte Benutzung liegt in der IndexedDB und nicht nur im Speicher der
    Seite.

    Sonst wäre die Sperre mit einem Neuladen weg – und ein Neuladen ist genau
    das, was jemand als Erstes tut, der vor einem fremden Bildschirm sitzt.
  */
  /*
    Der Sperrzustand auch als Referenz.

    Die Ereignishandler unten werden einmal angemeldet und sehen den Zustand
    aus ihrer Entstehungszeit. Ohne diese Referenz merkte sich `merken()`
    weiterhin Benutzung, während der Vorhang schon hängt – und genau das war
    der Fehler.
  */
  const gesperrtRef = useRef(false)
  gesperrtRef.current = gesperrt

  useEffect(() => {
    let abgebrochen = false

    /*
      Benutzung merken – aber NIE, solange gesperrt ist.

      Das ist die Korrektur des Produktionsbefunds. Im Sperrbildschirm steht
      ein Passwortfeld; jeder Tastendruck darin löste `keydown` aus und
      schrieb die aktuelle Zeit als «zuletzt benutzt». Ein einziges Zeichen
      und ein Neuladen genügten, und der Vorhang war weg – ohne dass je ein
      Passwort geprüft worden wäre.

      Gemessen und nachgestellt in `proben/sitzung-probe.mts`.
    */
    const merken = (): void => {
      if (gesperrtRef.current) return
      void setMeta(META_KEYS.letzteBenutzung, Date.now())
    }

    const sperren = (): void => {
      if (abgebrochen) return
      setGesperrt(true)
      melden.current?.(true)
      /* Der Zustand überlebt das Neuladen aus eigener Kraft. */
      void setMeta(META_KEYS.gesperrtSeit, Date.now())
    }

    const pruefen = async (): Promise<void> => {
      /*
        Drei Fragen, und eine einzige bejahte genügt. Fail-closed: Im Zweifel
        hängt der Vorhang.

          1. Steht der Sperrzustand noch von vorhin da?
          2. Liegt die letzte Benutzung an diesem Gerät zu lange zurück?
          3. Sagt der Server, die Sitzung sei zu lange unbenutzt gewesen?

        Die dritte Frage ist diejenige, die den Browser nicht braucht – und
        genau deshalb diejenige, die «Websitedaten löschen» überlebt. Sie trug
        bis vor kurzem nicht: `resolveSession` frischte `last_seen_at` bei
        JEDER aufgelösten Anfrage auf, auch bei der, mit der der Browser die
        Oberfläche selbst holt. Nach einem Neuladen meldete `/me` deshalb
        zwei Sekunden statt neunzig Minuten.

        Seit `zaehltAlsBenutzung()` in `http/app.ts` zählt das Ausliefern der
        Oberfläche nicht mehr als Benutzung, und `/me` selbst auch nicht. Die
        Antwort auf die dritte Frage kommt damit aus einer Quelle, an die
        niemand herankommt, der vor dem Gerät sitzt.
      */
      const [markiert, zuletzt] = await Promise.all([
        getMeta<number | null>(META_KEYS.gesperrtSeit, null),
        getMeta<number | null>(META_KEYS.letzteBenutzung, null)
      ])
      if (abgebrochen) return
      if (markiert !== null) return sperren()
      if (zuletzt !== null && Date.now() - zuletzt > SPERRE_NACH_MS) return sperren()
      if (typeof serverInaktivMs === 'number' && serverInaktivMs > SPERRE_NACH_MS) {
        return sperren()
      }
      /*
        Entschieden, und offen. Erst ab hier darf die Anwendung mit dem Server
        sprechen – vorher wüsste niemand, ob sie es überhaupt dürfte.
      */
      setFreigegeben(true)
      melden.current?.(false)
    }

    /*
      Erst prüfen, dann merken – und zwar nacheinander.

      Vorher stand hier `void pruefen(); merken()`. Das Merken lief los,
      während die Prüfung noch in der IndexedDB las, und konnte den alten
      Zeitstempel überschreiben, bevor er gelesen war. `merken()` sieht jetzt
      zusätzlich den Sperrzustand, den die Prüfung davor gesetzt hat.
    */
    void (async () => {
      await pruefen()
      merken()
    })()

    const takt = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') merken()
    }, TAKT_MS)

    const beiSichtwechsel = (): void => {
      if (document.visibilityState === 'hidden') merken()
      else void pruefen()
    }

    document.addEventListener('visibilitychange', beiSichtwechsel)
    window.addEventListener('focus', () => void pruefen())
    /* Jede echte Handlung zählt als Benutzung – nicht nur der Tab-Wechsel. */
    for (const art of ['pointerdown', 'keydown'] as const) {
      window.addEventListener(art, merken, { passive: true })
    }

    return () => {
      abgebrochen = true
      clearInterval(takt)
      document.removeEventListener('visibilitychange', beiSichtwechsel)
      for (const art of ['pointerdown', 'keydown'] as const) {
        window.removeEventListener(art, merken)
      }
    }
  }, [serverInaktivMs])

  useEffect(() => {
    if (gesperrt) feld.current?.focus()
  }, [gesperrt])

  async function entsperren(): Promise<void> {
    if (!passwort) return
    setLaeuft(true)
    setFehler(null)
    try {
      /*
        Der Server prüft das Passwort. Erst danach fällt der Vorhang – und
        zwar in dieser Reihenfolge: Merker löschen, Benutzung auffrischen,
        Zustand öffnen. Andersherum bliebe bei einem Absturz zwischen den
        Schritten ein offener Bildschirm mit gesetztem Sperrmerker zurück.
      */
      await api.entsperren(passwort, uuidv7())
      await setMeta(META_KEYS.gesperrtSeit, null)
      await setMeta(META_KEYS.letzteBenutzung, Date.now())
      setPasswort('')
      /*
        Die Auskunft des Servers ist ab jetzt überholt.

        Sie sagte «90 Minuten unbenutzt»; die Route hat `last_seen_at` eben
        auf jetzt gesetzt. Bliebe der alte Wert stehen, hinge der Vorhang beim
        nächsten Fensterwechsel wieder da – mit demselben Wert, für immer.

        Zurückgesetzt VOR dem Öffnen: Der Haken oben läuft auf diese Änderung
        hin erneut, und er soll einen Zustand vorfinden, in dem alle drei
        Fragen ehrlich mit Nein beantwortet werden.
      */
      setServerInaktivMs(undefined)
      setGesperrt(false)
      setFreigegeben(true)
      melden.current?.(false)
    } catch (e) {
      setFehler(fehlerText(e))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Abmelden – aus dem Sperrbildschirm heraus.
   *
   * Zuerst die Sperrspur löschen, dann abmelden. Umgekehrt bliebe bei einem
   * Abbruch zwischen beiden Schritten ein abgemeldetes Gerät mit gesetztem
   * Sperrmerker zurück – und der Nächste stünde wieder vor dem Vorhang, auf
   * den er kein Passwort hat. Die Abmeldung löscht die Merker ebenfalls; das
   * hier ist der Gürtel zum Hosenträger, für den Fall, dass die Abmeldung
   * auf halber Strecke scheitert.
   */
  async function abmelden(): Promise<void> {
    if (!aufAbmelden || meldetAb) return
    setMeldetAb(true)
    setFehler(null)
    try {
      await setMeta(META_KEYS.gesperrtSeit, null)
      await setMeta(META_KEYS.letzteBenutzung, Date.now())
      setGesperrt(false)
      melden.current?.(false)
      await aufAbmelden()
    } catch (e) {
      setFehler(fehlerText(e))
      setGesperrt(true)
      setMeldetAb(false)
    }
  }

  /*
    `office` gehört an den Vorhang selbst und nicht nur an das, was darunter
    liegt.

    Die Farbmarken – `--rp-bg`, `--rp-surface`, `--rp-border` – stehen in
    `office.css` unter `.office`, nicht unter `:root`. Solange der angemeldete
    Bereich mitgezeichnet wurde, erbte der Vorhang sie von dort. Seit er beim
    Laden alleine dasteht, erbt er nichts mehr und fiele auf die hellen
    Vorgaben aus `tokens.css` zurück: ein weisser Sperrbildschirm mitten in
    einer dunklen Anwendung.

    Derselbe Fehler wie bei der Control Plane in Paket 12, und dieselbe
    Behebung.
  */
  return (
    <>
      {freigegeben && kinder}
      {gesperrt && (
        <div
          className="office rp-sperre"
          id="sperrbildschirm"
          role="dialog"
          aria-modal="true"
        >
          <div className="rp-sperre-blatt">
            {/*
              Oben steht der BETRIEB, nicht die Plattform.

              Wer vor diesem Bildschirm steht, soll auf einen Blick sehen,
              wessen Bildschirm das ist – in einer Kita mit drei Bereichen
              hängen drei Geräte nebeneinander. Dieselbe Darstellung wie in
              der Seitenleiste: Logo, wenn eines hinterlegt ist, sonst die
              Kachel mit dem Anfangsbuchstaben. Raporto steht unten, klein.
            */}
            <div className="office-side-head rp-sperre-marke">
              {marke.logoKey && !logoFehlt ? (
                <img
                  className="office-logo"
                  id="sperre-logo"
                  src={logoUrl(marke.logoKey)}
                  alt={marke.firma}
                  onError={() => setLogoFehlt(true)}
                />
              ) : (
                <span className="office-betrieb">
                  <span className="office-betriebszeichen" aria-hidden="true">
                    {ersterBuchstabe(marke.firma)}
                  </span>
                  <span className="office-betriebsname" id="sperre-firma">
                    {marke.firma || 'Raporto'}
                  </span>
                </span>
              )}
            </div>

            <h2>Gesperrt</h2>
            <p>
              Angemeldet als <strong>{session.name}</strong>
              {marke.firma ? ` bei ${marke.firma}` : ''}. Dein Passwort öffnet den Bildschirm
              — es geht nichts verloren.
            </p>
            <label htmlFor="sperre-passwort">Passwort</label>
            <input
              id="sperre-passwort"
              ref={feld}
              type="password"
              autoComplete="current-password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void entsperren()
              }}
            />
            {fehler && (
              <div className="rp-sperre-fehler" id="sperre-fehler">
                {fehler}
              </div>
            )}
            <button
              type="button"
              className="rp-knopf rp-knopf-gold rp-knopf-gross"
              id="btn-sperre-oeffnen"
              disabled={laeuft || meldetAb || !passwort}
              onClick={() => void entsperren()}
            >
              {laeuft ? 'Wird geprüft …' : 'Weiterarbeiten'}
            </button>

            {/*
              Der Weg hinaus, sichtbar und nicht versteckt.

              Er steht unter dem Hauptknopf und nicht daneben: Der häufige
              Fall ist die Kaffeepause, und dafür tippt man sein Passwort.
              Der Knopf hier ist für den anderen Fall – falsches Konto,
              fremdes Gerät, vergessenes Passwort – und muss dann sofort zu
              finden sein, statt dass jemand Tabs schliesst und es wieder
              versucht.
            */}
            {aufAbmelden && (
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="btn-sperre-abmelden"
                disabled={meldetAb}
                onClick={() => void abmelden()}
              >
                {meldetAb ? 'Meldet ab …' : 'Abmelden — anderes Konto verwenden'}
              </button>
            )}

            {/*
              Raporto als Plattformmarke: unten, klein, und ein Ausgang mehr.

              Die Startseite ist öffentlich. Der angemeldete Bereich wird
              dabei nicht gezeichnet, und wer zurückkommt, steht wieder vor
              diesem Vorhang – die Merker liegen am Gerät, nicht am Fenster.
            */}
            <div className="rp-sperre-fuss">
              {aufStartseite ? (
                <button
                  type="button"
                  className="rp-sperre-plattform"
                  id="btn-sperre-startseite"
                  onClick={aufStartseite}
                  title="Zur öffentlichen Raporto-Startseite"
                >
                  <Marke groesse={18} />
                </button>
              ) : (
                <Marke groesse={18} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
